import React from "react";
import { useState, useEffect, useRef, useCallback } from "react";
import { useInView } from "react-intersection-observer";
import ResumeCard from "../components/ResumeCard";
import CompanyExperienceGroup from "../components/CompanyExperienceGroup";
import SkillsSection from "../components/SkillsSection";
import PageBackground from "../components/PageBackground";

const SECTION_IDS = ["experience", "skills", "education"];

/** Mobile scroll-spy: distance from viewport top for the “active section” line (px). Larger = highlight switches sooner while scrolling. */
const MOBILE_SPY_OFFSET_HEADER_VISIBLE = 250;
const MOBILE_SPY_OFFSET_HEADER_HIDDEN = 130;
/** Web/Desktop scroll-spy: distance from viewport top for the "active section" line (px). Larger = highlight switches sooner while scrolling. */
const WEB_SPY_OFFSET_HEADER_VISIBLE = 220;
const WEB_SPY_OFFSET_HEADER_HIDDEN = 110;
const Resume = () => {
  const [activeSection, setActiveSection] = useState("experience");
  /** Mobile: site header is in view — section bar sticks below it (top-16); when header scrolls off, bar sticks to top (top-0). */
  const [siteHeaderInView, setSiteHeaderInView] = useState(true);
  const siteHeaderInViewRef = useRef(true);
  /** While true, scroll-spy ignores scroll events so nav click highlight is not overwritten mid smooth-scroll */
  const scrollSpyPausedRef = useRef(false);
  /** Section id from last nav click; until cleared, scroll-spy won't apply a *different* id (fixes mobile flash) */
  const programmaticScrollTargetRef = useRef(null);

  const computeActiveSectionFromScroll = useCallback(() => {
    if (typeof window === "undefined") return SECTION_IDS[0];

    const doc = document.documentElement;
    const mobile = window.innerWidth < 768;
    const offset = mobile
      ? siteHeaderInViewRef.current
        ? MOBILE_SPY_OFFSET_HEADER_VISIBLE
        : MOBILE_SPY_OFFSET_HEADER_HIDDEN
      : siteHeaderInViewRef.current
        ? WEB_SPY_OFFSET_HEADER_VISIBLE
        : WEB_SPY_OFFSET_HEADER_HIDDEN;
    const vh = window.innerHeight;
    const scrollY = window.scrollY;
    const scrollBottom = scrollY + vh;

    // 1) At the end of the page there is often no room to scroll the last section's
    //    top past the anchor line — treat as the last section being active.
    if (scrollBottom >= doc.scrollHeight - 3) {
      return SECTION_IDS[SECTION_IDS.length - 1];
    }

    // 2) If the middle section has fully scrolled away and the last section is
    //    visible, the anchor line never reaches the last block — activate it.
    if (SECTION_IDS.length >= 2) {
      const lastId = SECTION_IDS[SECTION_IDS.length - 1];
      const prevId = SECTION_IDS[SECTION_IDS.length - 2];
      const lastEl = document.getElementById(lastId);
      const prevEl = document.getElementById(prevId);
      if (lastEl && prevEl) {
        const prevRect = prevEl.getBoundingClientRect();
        const lastRect = lastEl.getBoundingClientRect();
        const prevFullyAbove = prevRect.bottom <= 0;
        const lastIntersectsViewport = lastRect.top < vh && lastRect.bottom > 0;
        if (prevFullyAbove && lastIntersectsViewport) {
          return lastId;
        }
      }
    }

    // 3) Default: last section whose top has crossed the anchor from above.
    let current = SECTION_IDS[0];
    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;
      if (el.getBoundingClientRect().top <= offset) {
        current = id;
      }
    });
    return current;
  }, []);

  // Update highlighted nav while the user scrolls (matches which section is "current" at the top)
  useEffect(() => {
    const handleScroll = () => {
      if (scrollSpyPausedRef.current) return;
      const computed = computeActiveSectionFromScroll();
      const target = programmaticScrollTargetRef.current;
      // During smooth scroll to a tab, geometry often still says the *previous* section for a while.
      // Don't apply that to the nav highlight or it flashes (especially on mobile).
      if (target && computed !== target) {
        return;
      }
      if (target && computed === target) {
        programmaticScrollTargetRef.current = null;
      }
      setActiveSection(computed);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, [computeActiveSectionFromScroll]);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const headerEl = document.querySelector("header.ant-layout-header");
    if (!headerEl) return undefined;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        const visible = entry.isIntersecting;
        siteHeaderInViewRef.current = visible;
        setSiteHeaderInView(visible);
      },
      { root: null, threshold: 0, rootMargin: "0px" },
    );

    const sync = () => {
      io.observe(headerEl);
      const rect = headerEl.getBoundingClientRect();
      const visible = rect.bottom > 0 && rect.top < (window.innerHeight || 0);
      siteHeaderInViewRef.current = visible;
      setSiteHeaderInView(visible);
    };

    sync();
    mq.addEventListener("change", sync);
    return () => {
      mq.removeEventListener("change", sync);
      io.disconnect();
    };
  }, []);

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      window.dispatchEvent(new Event("scroll"));
    });
    return () => cancelAnimationFrame(id);
  }, [siteHeaderInView]);

  const scrollToSection = useCallback((sectionId) => {
    setActiveSection(sectionId);
    const el = document.getElementById(sectionId);
    if (!el) return;

    programmaticScrollTargetRef.current = sectionId;
    scrollSpyPausedRef.current = true;

    // Only scroll to top if clicking "experience", otherwise scroll to section
    if (sectionId === "experience") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    let resumed = false;
    const resumeSpy = () => {
      if (resumed) return;
      resumed = true;
      setActiveSection(sectionId);
      window.setTimeout(() => {
        scrollSpyPausedRef.current = false;
        // Let scroll-spy take over again after geometry can match the target; until then
        // handleScroll ignores computed !== target via programmaticScrollTargetRef.
        window.setTimeout(() => {
          programmaticScrollTargetRef.current = null;
        }, 700);
      }, 120);
    };

    window.setTimeout(resumeSpy, 900);
    window.addEventListener("scrollend", resumeSpy, { once: true });
  }, []);

  return (
    <div className="relative flex w-full flex-col">
      <PageBackground subtle />
      {/* Sticky horizontal tabs: sticky below site header while header is visible; lock to top when header has scrolled away */}
      <div
        className={`sticky z-30 w-full border-b border-ink-100 dark:border-ink-800 ${
          siteHeaderInView
            ? "top-[4.25rem] bg-white/95 backdrop-blur-md shadow-sm dark:bg-ink-950/95 md:top-16"
            : "top-0 bg-white shadow-md dark:bg-ink-950"
        }`}
      >
        <div className="flex w-full justify-center">
          <ul className="grid w-full grid-cols-3 gap-2 px-3 py-2 text-center md:max-w-[950px] md:px-8">
            <li
              className={`nav-tab ${
                activeSection === "experience"
                  ? "nav-tab-active"
                  : "nav-tab-inactive"
              }`}
              onClick={() => scrollToSection("experience")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  scrollToSection("experience");
                }
              }}
            >
              Experience
            </li>
            <li
              className={`nav-tab ${
                activeSection === "skills"
                  ? "nav-tab-active"
                  : "nav-tab-inactive"
              }`}
              onClick={() => scrollToSection("skills")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  scrollToSection("skills");
                }
              }}
            >
              Skills
            </li>
            <li
              className={`nav-tab ${
                activeSection === "education"
                  ? "nav-tab-active"
                  : "nav-tab-inactive"
              }`}
              onClick={() => scrollToSection("education")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  scrollToSection("education");
                }
              }}
            >
              Education
            </li>
          </ul>
        </div>
      </div>

      {/* Main column: centered content */}
      <div className="relative z-10 flex w-full min-w-0 justify-center">
        <div className="w-full max-w-[950px] p-4 md:p-8 md:pt-0">
          <Section
            name="experience"
            id="experience"
            className="mb-16 md:mb-24 lg:mb-32"
          >
            <h2
              className={`section-heading mb-6 ${
                activeSection === "experience" ? "section-heading-active" : ""
              }`}
            >
              Experience
            </h2>
            <div className="space-y-6">
              <ResumeCard
                title="Software Engineer"
                fromDate="October 2026"
                toDate="Present"
                companyName="Stripe"
                companyLogo="images/stripe_logo.svg"
                location="Vancouver, British Columbia (Remote)"
                description="Joining Stripe to help increase the GDP of the internet."
              />
              <CompanyExperienceGroup
                companyName="Dayforce"
                companyLogo="images/dayforce_logo.jpeg"
                fromDate="September 2021"
                toDate="October 2026"
                roles={[
                  {
                    title: "Lead Software Engineer",
                    fromDate: "November 2025",
                    toDate: "October 2026",
                    location: "Vancouver, British Columbia (Remote)",
                    description:
                      "Worked on developing scalable web applications.",
                    highlights: [
                      <>
                        Architected and executed a{" "}
                        <strong>
                          full-scale infrastructure migration from MongoDB to
                          CosmosDB
                        </strong>
                        , ensuring full data compliance throughout and
                        delivering zero data loss across the transition.
                      </>,
                      <>
                        Developed an <strong>AI agent</strong> adopted team-wide
                        to standardize and streamline logging across all
                        microservices, significantly{" "}
                        <strong>
                          improving observability and reducing production
                          incidents
                        </strong>
                        .
                      </>,
                      <>
                        Built a custom GPT trained on all internal team wikis
                        and documentation, enabling the team to surface
                        information faster and{" "}
                        <strong>
                          accelerate onboarding and decision-making
                        </strong>
                        .
                      </>,
                      <>
                        Currently spearheading an initiative to extract legacy
                        monolithic code into a centralized, reusable
                        microservice,{" "}
                        <strong>
                          improving system reliability, reducing duplication,
                          and increasing overall platform performance
                        </strong>
                        .
                      </>,
                    ],
                    relevantTechnologies: [
                      "React",
                      "NextJS",
                      "MongoDB",
                      "CosmosDB",
                      "Kubernetes",
                      "Kafka",
                      ".NET",
                    ],
                  },
                  {
                    title: "Senior Software Engineer",
                    fromDate: "November 2023",
                    toDate: "November 2025",
                    location: "Vancouver, British Columbia (Remote)",
                    description:
                      "Worked on developing scalable web applications.",
                    highlights: [
                      <>
                        Implemented{" "}
                        <strong>Service-to-Service authentication</strong>{" "}
                        across all microservices, strengthening security posture
                        and enforcing least-privilege access patterns
                        platform-wide.
                      </>,
                      <>
                        Led multiple end-to-end product initiatives from
                        ideation through delivery via cross-functional
                        collaboration with Product, Design, and Legal, resulting
                        in features adopted by millions of users.
                      </>,
                      <>
                        Mentored and developed junior engineers through regular
                        code reviews, technical guidance, and knowledge sharing,
                        accelerating their growth and overall team velocity.
                      </>,
                    ],
                    relevantTechnologies: [
                      "React",
                      "NextJS",
                      "MongoDB",
                      "IdentityServer",
                      "Docker",
                      "Kafka",
                      ".NET",
                    ],
                  },
                  {
                    title: "Software Engineer II",
                    fromDate: "May 2022",
                    toDate: "November 2023",
                    location: "Toronto, Ontario (Remote)",
                    description:
                      "Worked on developing scalable web applications.",
                    highlights: [
                      <>
                        Led feature development across the candidate-facing
                        career portal using{" "}
                        <strong>
                          React, Next.js, TypeScript, MongoDB, C#, and .NET
                        </strong>
                        , contributing to <strong>$1.51 billion</strong> in
                        annual revenue.
                      </>,
                      <>
                        Redesigned search architecture, reducing query execution
                        time from 200ms–16s under load to a consistent 15ms–25ms
                        — a <strong>90%+ improvement</strong> that directly
                        enhanced user experience at scale.
                      </>,
                      <>
                        Drove UI test coverage from <strong>40% to 85%</strong>{" "}
                        by implementing <strong>SonarQube</strong> and rolling
                        out a team-wide coverage initiative, significantly
                        reducing regression and production bugs.
                      </>,
                    ],
                    relevantTechnologies: [
                      "React",
                      "NextJS",
                      "MongoDB",
                      "Specflow",
                      "Kafka",
                      ".NET",
                      "SQL",
                    ],
                  },
                  {
                    title: "Software Engineer I",
                    fromDate: "September 2021",
                    toDate: "May 2022",
                    location: "Toronto, Ontario (Remote)",
                    description:
                      "Worked on developing scalable web applications.",
                    highlights: [
                      <>
                        Implemented localization across the entire
                        authentication flow, building a reusable solution
                        adopted by multiple microservice teams and supporting
                        over 50 languages.
                      </>,
                      <>
                        Introduced functional test coverage into a new project,
                        integrating automated quality checks directly into the
                        CI/CD pipeline and raising overall{" "}
                        <strong>code reliability</strong> from day one.
                      </>,
                      <>
                        Engineered independent, highly-tested{" "}
                        <strong>microservices</strong> to ensure optimal
                        scalability, maintainability, and resiliency, enabling
                        smooth operations in a high-traffic environment.
                      </>,
                      <>
                        Leveraged <strong>Azure cloud functions</strong>, key
                        vaults, service bus, and other Azure services to
                        maximize scalability, enabling Dayforce to serve over 5
                        million active users.
                      </>,
                    ],
                    relevantTechnologies: [
                      "React",
                      "NextJS",
                      "MongoDB",
                      "Specflow",
                      "Kafka",
                      ".NET",
                      "SQL",
                      "Dojo Toolkit",
                    ],
                  },
                ]}
              />

              <ResumeCard
                title="iOS Developer Intern"
                fromDate="September 2020"
                toDate="December 2020"
                companyName="ForaHealthyMe"
                companyLogo="images/forahealthyme.jpeg"
                location="Toronto, Ontario (Remote)"
                description="Developed a native iOS application using Swift."
                highlights={[
                  "Worked on an iOS application built natively in Swift.",
                  "Implemented designs provided by product.",
                  "Fully built out the iOS application working closely with backend developers.",
                  "Optimized database queries for performance improvements.",
                ]}
                relevantTechnologies={["Swift"]}
              />
            </div>
          </Section>

          <Section
            name="skills"
            id="skills"
            className="mb-16 md:mb-24 lg:mb-32"
          >
            <h2
              className={`section-heading mb-6 ${
                activeSection === "skills" ? "section-heading-active" : ""
              }`}
            >
              Skills
            </h2>
            <SkillsSection />
          </Section>

          <Section
            name="education"
            id="education"
            className="mb-16 md:mb-24 lg:mb-32"
          >
            <h2
              className={`section-heading mb-6 ${
                activeSection === "education" ? "section-heading-active" : ""
              }`}
            >
              Education
            </h2>
            <ResumeCard
              title="Honours Bachelor of Technology – Software Development"
              fromDate="September 2017"
              toDate="August 2021"
              companyName="Seneca Polytechnic"
              companyLogo="images/seneca_college_logo.jpeg"
              location="Toronto, Ontario"
              description="I graduated with Honours from Seneca Polytechnic where I studied software development and explored various computer science concepts."
            />
          </Section>
        </div>
      </div>
    </div>
  );
};

// Section component for fade-in effect
const Section = ({ name, id, children, className }) => {
  const { ref, inView } = useInView({
    triggerOnce: true, // Trigger the effect only once
    threshold: 0.1, // Percentage of the element that needs to be visible to trigger the effect
  });

  return (
    <div
      name={name}
      id={id}
      className={`transition-opacity duration-1000 scroll-mt-36 md:scroll-mt-24 ${
        inView ? "opacity-100" : "opacity-0"
      } ${className}`}
      ref={ref}
    >
      {children}
    </div>
  );
};

export default Resume;
